import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  CircularProgress, 
  Alert,
  AppBar,
  Toolbar,
  Button,
  Link
} from '@mui/material';
import PuckRenderer from '../../components/MicroSite/PuckRenderer';
import AnalyticsTracker from '../../components/Analytics/AnalyticsTracker';

const MicroSitePage = () => {
  const { subdomain, slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [site, setSite] = useState(null);
  const [page, setPage] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch the page data from the public API
        const apiUrl = `/api/public/sites/${subdomain}/pages/${slug}`;
        console.log('Fetching page data from:', apiUrl);
        
        const response = await fetch(apiUrl, {
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch page: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Fetched page data:', data);
        
        setSite(data.site);
        setPage(data.page);
      } catch (err) {
        console.error('Error fetching page:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPageData();
  }, [subdomain, slug]);

  // Set page title and meta description
  useEffect(() => {
    if (page) {
      document.title = `${page.title} | ${site?.name || 'Website'}`;
      
      // Update meta description if available
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription && page.metaDescription) {
        metaDescription.setAttribute('content', page.metaDescription);
      }
    }
  }, [page, site]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body1">
          Sorry, we couldn't load this page. Please try again later.
        </Typography>
      </Container>
    );
  }

  if (!page || !site) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Page not found
        </Alert>
        <Typography variant="body1">
          The page you're looking for doesn't exist or has been moved.
        </Typography>
      </Container>
    );
  }

  // Apply site theme if available
  const theme = site.theme || {};
  const primaryColor = theme.primaryColor || '#1976d2';
  const secondaryColor = theme.secondaryColor || '#f50057';
  const backgroundColor = theme.backgroundColor || '#ffffff';
  const textColor = theme.textColor || '#333333';

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: backgroundColor,
      color: textColor
    }}>
      {/* Site Header */}
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: primaryColor,
          color: '#ffffff'
        }}
      >
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {site.name}
          </Typography>
          
          {/* Navigation links - these would come from site.settings */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button color="inherit" component={Link} href={`/sites/${subdomain}`}>
              Home
            </Button>
            {site.settings?.navigation?.map((item, index) => (
              <Button 
                key={index}
                color="inherit" 
                component={Link} 
                href={`/sites/${subdomain}/pages/${item.slug}`}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Page Content */}
      <Box component="main">
        {/* Render the Puck content */}
        <PuckRenderer 
          content={page.content} 
          accountId={site.accountId}
          microSiteId={site.id}
          pageId={page.id}
        />
        
        {/* Add the analytics tracker */}
        {site && page && (
          <AnalyticsTracker
            pageId={page.id}
            microSiteId={site.id}
            accountId={site.accountId}
            trackInteractions={true}
          />
        )}
      </Box>
      
      {/* Site Footer */}
      <Box 
        component="footer" 
        sx={{ 
          py: 3, 
          px: 2, 
          mt: 'auto', 
          backgroundColor: theme.footerColor || primaryColor,
          color: '#ffffff'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" align="center">
            {site.settings?.footerText || `© ${new Date().getFullYear()} ${site.name}`}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default MicroSitePage; 